import React, { useState, useEffect } from 'react';
import {
    Modal,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Box,
    Typography
} from '@mui/material';
import { LocalizationProvider, TimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ReactSelect from 'react-select';
import dayjs from 'dayjs';
import axios from 'axios';


const repeatOptions = [
    { value: "Monday", label: "Monday" },
    { value: "Tuesday", label: "Tuesday" },
    { value: "Wednesday", label: "Wednesday" },
    { value: "Thursday", label: "Thursday" },
    { value: "Friday", label: "Friday" },
    { value: "Saturday", label: "Saturday" },
    { value: "Sunday", label: "Sunday" },
    { value: "First Saturday", label: "First Saturday" },
    { value: "Second Saturday", label: "Second Saturday" },
    { value: "Third Saturday", label: "Third Saturday" },
    { value: "Fourth Saturday", label: "Fourth Saturday" },
];

export default function SubshiftEditModal({
    open,
    onClose,
    shiftId,
    onUpdateSuccess,
    toast
}) {
    // State for form fields
    const [shiftName, setShiftName] = useState('');
    const [shiftType, setShiftType] = useState('');
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [workingHours, setWorkingHours] = useState(0);
    const [cutOffLeaveType, setCutOffLeaveType] = useState('');
    const [cutOffTime, setCutOffTime] = useState(null);
    const [effectiveFrom, setEffectiveFrom] = useState(null);
    const [priority, setPriority] = useState(1);
    const [repeatDays, setRepeatDays] = useState(false);
    const [selectedRepeatDays, setSelectedRepeatDays] = useState([]);
    const [specificDate, setSpecificDate] = useState(null);

    function convertTimeToDecimal(time) {
        const [hours, minutes] = time.split(":").map(Number);
        const decimalTime = hours + (minutes / 60);
        return decimalTime.toFixed(2); // Keeps two decimal places
    }

    function convertToTimeFormat(time) {
        let [hours, minutes] = time.split('.').map(Number);
        minutes = Math.round((minutes / 100) * 60); // Convert decimal to minutes
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
    }

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: 600,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
    };

    // Fetch initial data when modal opens
    useEffect(() => {
        if (open && shiftId) {
            const fetchShiftDetails = async () => {
                const token = localStorage.getItem("accessToken");
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_API}/api/employee/shiftlines/${shiftId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                    const data = response.data;

                    // Populate form fields
                    setShiftName(data.shift_name || '');
                    setShiftType(data.shift_type || '');
                    setStartTime(data.start_time ? dayjs(data.start_time, 'HH:mm') : null);
                    setEndTime(data.end_time ? dayjs(data.end_time, 'HH:mm') : null);
                    setWorkingHours(convertTimeToDecimal(data.working_hours) || 0);
                    setCutOffLeaveType(data.leave_type || '');
                    setCutOffTime(data.cut_off_time ? dayjs(data.cut_off_time, 'HH:mm') : null);
                    setEffectiveFrom(data.effective_date ? dayjs(data.effective_date) : null);
                    setPriority(data.priority || 1);

                    // Handle repeat days logic
                    if (data.repeat_on && data.repeat_on.length > 0) {
                        setRepeatDays(true);
                        setSelectedRepeatDays(
                            repeatOptions.filter(option =>
                                data.repeat_on.includes(option.value)
                            )
                        );
                    } else {
                        setRepeatDays(false);
                        setSpecificDate(data.specific_date ? dayjs(data.specific_date) : null);
                    }
                } catch (error) {
                    console.error("Error fetching shift details:", error);
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to fetch shift details. Please try again.',
                        life: 3000,
                    });
                }
            };

            fetchShiftDetails();
        }
    }, [open, shiftId]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem("accessToken");

        // Prepare data for submission
        const submitData = {
            shift_name: shiftName,
            shift_type: shiftType,
            start_time: startTime ? startTime.format('HH:mm:ss') : null,
            end_time: endTime ? endTime.format('HH:mm:ss') : null,
            working_hours: convertToTimeFormat(workingHours),
            leave_type: cutOffLeaveType,
            cut_off_time: cutOffTime ? cutOffTime.format('HH:mm:ss') : null,
            effective_date: effectiveFrom ? effectiveFrom.format('YYYY-MM-DD') : null,
            priority: priority,
            repeat_on: repeatDays ? selectedRepeatDays.map(day => day.value) : [],
            specific_date: !repeatDays && specificDate ? specificDate.format('YYYY-MM-DD') : null
        };

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API}/api/employee/shiftlines/${shiftId}/`,
                submitData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                }
            );

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Shift updated successfully!',
                life: 3000
            });

            // Call the success callback to potentially refresh data
            onUpdateSuccess && onUpdateSuccess();

            // Close the modal
            onClose();
        } catch (error) {
            console.error("Error updating shift:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update shift. Please try again.',
                life: 3000
            });
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="edit-shift-modal"
        >
            <Box sx={style} component="form" onSubmit={handleSubmit}>
                <Typography id="shift-edit-modal" variant="h6" component="h2">
                    Edit Sub Shift Details
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        label="Enter Shift Name"
                        variant="outlined"
                        fullWidth
                        style={{ maxWidth: "48%" }}
                        value={shiftName}
                        onChange={(e) => setShiftName(e.target.value)}
                        required
                    />

                    <FormControl fullWidth style={{ maxWidth: "48%" }}>
                        <InputLabel>Shift Type</InputLabel>
                        <Select
                            value={shiftType}
                            onChange={(e) => setShiftType(e.target.value)}
                            required
                        >
                            <MenuItem value="Flexible">Flexible</MenuItem>
                            <MenuItem value="Fixed">Fixed</MenuItem>
                        </Select>
                    </FormControl>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                            <TimePicker
                                label="Start Time"
                                value={startTime}
                                onChange={(newValue) => setStartTime(newValue)}
                                renderInput={(params) => <TextField {...params} fullWidth sx={{ maxWidth: '48%' }} />}
                                required
                            />
                        </div>
                        <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                            <TimePicker
                                label="End Time"
                                value={endTime}
                                onChange={(newValue) => setEndTime(newValue)}
                                renderInput={(params) => <TextField {...params} fullWidth sx={{ maxWidth: '48%' }} />}
                                required
                            />
                        </div>
                        <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                            <TextField
                                label="Working Hours"
                                variant="outlined"
                                fullWidth
                                type="number"
                                value={workingHours}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    if (value >= 0) setWorkingHours(value);
                                }}
                                inputProps={{ min: 0 }}
                                required
                            />
                        </div>

                        <FormControl fullWidth style={{ maxWidth: "48%" }}>
                            <InputLabel>Cut-Off Leave Type</InputLabel>
                            <Select
                                value={cutOffLeaveType}
                                onChange={(e) => setCutOffLeaveType(e.target.value)}
                                required
                            >
                                <MenuItem value="Quarter-day">Quarter</MenuItem>
                                <MenuItem value="Half-day">Half</MenuItem>
                            </Select>
                        </FormControl>

                        <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                            <TimePicker
                                label="Cut-Off Time"
                                value={cutOffTime}
                                onChange={(newValue) => setCutOffTime(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        disabled={shiftType !== "Flexible"}
                                    />
                                )}
                                required={shiftType === "Flexible"}
                            />
                        </div>

                        <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                            <DatePicker
                                label="Effective From"
                                value={effectiveFrom}
                                onChange={(newValue) => setEffectiveFrom(newValue)}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                                required
                            />
                        </div>
                    </LocalizationProvider>


                    <TextField
                        label="Priority"
                        type="number"
                        variant="outlined"
                        fullWidth
                        style={{ maxWidth: "48%" }}
                        value={priority}
                        onChange={(e) => setPriority(Number(e.target.value) || 1)}
                        inputProps={{ min: 1 }}
                        required
                    />

                    {repeatDays ? (
                        <div style={{ width: '48%' }}>
                            <ReactSelect
                                options={repeatOptions}
                                isMulti
                                value={selectedRepeatDays}
                                onChange={(selected) => setSelectedRepeatDays(selected)}
                                placeholder="Select repeat days"
                                required
                            />
                        </div>
                    ) : (
                        <div style={{ width: '48%' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Specific Date"
                                    value={specificDate}
                                    onChange={(newValue) => setSpecificDate(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    required
                                />
                            </LocalizationProvider>
                        </div>
                    )}
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={repeatDays}
                            onChange={(e) => setRepeatDays(e.target.checked)}
                        />
                    }
                    label="Repeat Days"
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                    >
                        Update Shift
                    </button>
                </Box>
            </Box>
        </Modal >
    );
}